import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnInit, signal,
  Signal,
  TemplateRef,
  viewChild, WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AlertComponent } from '@libs/common/components';
import { SCANNER_ROUTE_NAME } from '@libs/shared/models';
import { CartService, ShoppingListScannerHandlerService, ShoppingListsState, ViewportService } from '@libs/shared/services';
import { CartTrackingService, DownloadTrackingService } from '@libs/shared/tracking';
import {
  CartItemPayloadModel,
  ShoppingListItemModel,
  ShoppingListModel,
} from '@libs/shop/models';
import { DialogService } from '@libs/shared/common';
import { firstValueFrom } from 'rxjs';
import { ShoppingListItemsComponent } from './shopping-list-items/shopping-list-items.component';
import { ShoppingListHeaderComponent } from './shopping-list-header/shopping-list-header.component';
import { AddToShoppingListQuickComponent } from './add-to-shopping-list-quick';
import { CreateAndEditShoppingListDialogComponent } from './create-and-edit-shopping-list-dialog';

@Component({
  selector: 'ui-shopping-list',
  templateUrl: './shopping-list.component.html',
  styleUrls: ['./shopping-list.component.scss'],
  imports: [
    AddToShoppingListQuickComponent,
    ShoppingListItemsComponent,
    ShoppingListHeaderComponent,
    AlertComponent,
    CreateAndEditShoppingListDialogComponent,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingListComponent implements OnInit {
  public listId: InputSignal<string> = input.required();

  public shoppingList: Signal<ShoppingListModel> = computed(() => this.shoppingListState.entityMap()[this.listId()]);
  public error: Signal<Error | undefined> = computed(() => this.shoppingListState.error() ?? this.shoppingListState.loadShoppingListByIdError());
  public isLoading: Signal<boolean> = computed(() => this.shoppingListState.isLoading() || this.shoppingListState.loadShoppingListByIdIsPending());

  public items: Signal<ShoppingListItemModel[]> = computed(
    () => this.shoppingList().items,
  );

  public isMobile: Signal<boolean>;
  public isDownloading: WritableSignal<boolean> = signal(false);

  private readonly shoppingListEditDialog: Signal<CreateAndEditShoppingListDialogComponent> = viewChild.required(CreateAndEditShoppingListDialogComponent);
  private readonly shoppingListItemsComponent: Signal<ShoppingListItemsComponent> = viewChild.required(ShoppingListItemsComponent);
  private readonly addToShoppingListQuickTemplate: Signal<TemplateRef<unknown>> = viewChild.required('addToShoppingListQuick', { read: TemplateRef });
  private dialogRef?: MatDialogRef<unknown>;
  private readonly cartTrackingService: CartTrackingService = inject(CartTrackingService);
  private readonly downloadTracking: DownloadTrackingService = inject(DownloadTrackingService);

  public constructor(
    private readonly shoppingListState: ShoppingListsState,
    private readonly cartService: CartService,
    private readonly dialog: DialogService,
    private readonly viewportService: ViewportService,
    private readonly router: Router,
  ) {
    this.isMobile = toSignal(this.viewportService.isMobile(), { initialValue: false });
  }

  public ngOnInit(): void {
    void this.shoppingListState.loadShoppingListById(this.listId());
  }

  public async editShoppingListName(): Promise<void> {
    await this.shoppingListEditDialog().openDialog();
  }

  public async addProductToShoppingListQuick([sku, quantity]: [string, number]): Promise<void> {
    this.dialogRef?.close();

    await this.shoppingListState.addProductToShoppingList({ listId: this.listId(), sku: sku, quantity: quantity });
  }

  public openScanner(): void {
    this.dialog.closeAll();
    void this.router.navigate([SCANNER_ROUTE_NAME, ShoppingListScannerHandlerService.Key, this.listId()]);
  }

  public async onItemQuantityChanged(payload: ShoppingListItemModel): Promise<void> {
    await this.shoppingListState.updateProductInShoppingList({ listId: this.listId(), item: payload });
  }

  public async onRemoveItems(payload: ShoppingListItemModel | ShoppingListItemModel[]): Promise<void> {
    if (Array.isArray(payload)) {
      await this.shoppingListState.deleteProductsFromShoppingListCollection({ listId: this.listId(), items: payload });
      this.shoppingListItemsComponent().clearOrCloseSelection();

      return;
    }

    await this.shoppingListState.deleteProductFromShoppingList({ listId: this.listId(), item: payload });
  }

  public onItemsAddToCart(payload: ShoppingListItemModel[]): void {
    const collection: CartItemPayloadModel[] = payload.map((item) => {
      return {
        type: 'cart-item',
        id: item.sku,
        quantity: item.quantity,
      };
    });

    void firstValueFrom(this.cartService.addItemsCollection(collection))
      .then((result) => {
        this.cartTrackingService.trackAddToCartListItems(payload, result.errors);
        this.shoppingListItemsComponent().clearOrCloseSelection();
      });
  }

  public openAddToShoppingListQuick(): void {
    this.dialogRef = this.dialog.open(this.addToShoppingListQuickTemplate(), {
      width: '630px',
      panelClass: 'flex-dialog-container',
      restoreFocus: false,
      position: {
        top: 'calc(15vh + env(safe-area-inset-top))',
      },
    });
  }

  public download(): void {
    this.isDownloading.set(true);
    void this.shoppingListState.downloadShoppingList(this.listId())
      .then((fileName: string) => void this.downloadTracking.trackDownload(fileName))
      .finally(() => void this.isDownloading.set(false));
  }
}
